import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Link,
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min'
import {
  Box,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core'
import Icon from '@mdi/react'
import { mdiDelete, mdiNoteEditOutline } from '@mdi/js'

import { remove } from '../SupplierActions'
import BasePopUp from '../../../components/hooks/BasePopUp'
import { useQueryParams } from '../../../util/utils'
import { usePermissions } from '../../../hooks/usePermission'
import { getSorting, stableSort } from '../../../util/TableUtils'
import {
  StyledTableCellBody,
  StyledTableCellHeader,
} from '../../../components/hooks/BaseTableComponents'
import Pagination from '../../../components/hooks/BasePagination'
import EnhancedTableHead from '../../components/TableHead'
import colors from '../../../assets/colors'

const useStyles = makeStyles({
  status: {
    textAlign: 'center',
    fontSize: '14px',
    borderRadius: '10px',
    padding: '4px 16px',
    maxWidth: '80px',
    backgroundColor: colors.graylight,
    color: '#000',
  },
  statusAtivo: {
    color: '#FFFF',
    backgroundColor: colors.secondary,
  },
})

function createData(
  id,
  name,
  status,
  address,
  phone,
  cpf_cnpj,
  email,
  knowWay
) {
  return {
    id,
    name,
    status,
    address,
    phone,
    cpf_cnpj,
    email,
    knowWay,
    update: 'update-supplier',
    delete: 'delete-supplier',
    link_update: `/supplier/${id}/edit`,
  }
}

const headCells = [
  { id: 'id', numeric: false, disablePadding: true, label: 'ID' },
  { id: 'name', numeric: true, disablePadding: false, label: 'Nome' },
  { id: 'status', numeric: true, disablePadding: false, label: 'Status' },
  { id: 'address', numeric: true, disablePadding: false, label: 'Endereço' },
  { id: 'phone', numeric: true, disablePadding: false, label: 'Telefone' },
  { id: 'cpf_cnpj', numeric: true, disablePadding: false, label: 'CPF / CNPJ' },
  { id: 'email', numeric: true, disablePadding: false, label: 'E-mail' },
  {
    id: 'knowWay',
    numeric: false,
    disablePadding: false,
    label: 'Como conheceu',
  },
  {
    id: 'actions',
    numeric: true,
    disablePadding: false,
    label: 'Ações',
    align: 'center',
  },
]

const renderKnowWays = (knowWays = []) => {
  if (!knowWays?.length) return '-'
  return knowWays?.map(knowWay => knowWay?.name).join(', ')
}

function List(props) {
  const { classes } = props
  const { items, meta } = useSelector(state => state.supplier)
  const permissionUser = useSelector(state => state.auth.permissionUser)
  const { canUpdate, canDelete } = usePermissions()

  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  const filters = useQueryParams(location.search)

  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('name')
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [supplierToDelete, setSupplierToDelete] = useState(null)

  const styles = useStyles()

  const handleSubmit = data => {
    const searchParams = new URLSearchParams(data || filters)
    history.push(`${location?.pathname}?${searchParams?.toString()}`)
  }

  const handleChangeRowsPerPage = event => {
    const data = { ...filters, rows: event.target.value, page: 1 }
    handleSubmit(data)
  }

  const onMovePage = (event, newPage) => {
    const data = { ...filters, page: newPage }
    handleSubmit(data)
  }

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === 'desc'
    setOrder(isDesc ? 'asc' : 'desc')
    setOrderBy(property)
  }

  const handleOpenDeleteModal = (id = null) => {
    if (id) {
      setSupplierToDelete(id)
    }
    setOpenDeleteModal(!openDeleteModal)
  }

  const handleConfirmDelete = async () => {
    if (supplierToDelete) {
      await dispatch(remove(supplierToDelete))
      setOpenDeleteModal(false)
      setSupplierToDelete(null)
      handleSubmit(filters)
    }
  }

  const rows = items.map(item => {
    let address = '-'
    if (item.street && item.number && item.neighborhood && item.city) {
      address = `${item.street}, ${item.number}, ${item.neighborhood} - ${item.city}/${item.uf}`
    }

    return createData(
      item.id,
      item.name,
      item.status_text,
      address,
      item.phone || '-',
      item.cpf_cnpj || '-',
      item.email || '-',
      renderKnowWays(item?.know_ways)
    )
  })

  return (
    <>
      <TableContainer className={classes.tableStickHeader}>
        <Table size="small" className={classes.table}>
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={items.length}
            headCells={headCells}
            hideID
          />
          <TableBody>
            {stableSort(rows, getSorting(order, orderBy)).map(row => (
              <TableRow hover tabIndex={-1} key={row.id}>
                <StyledTableCellHeader padding="checkbox" />

                <StyledTableCellBody
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'hidden',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  <Tooltip title={row?.name}>
                    <span>{row?.name}</span>
                  </Tooltip>
                </StyledTableCellBody>

                <StyledTableCellBody>
                  {row.status.toLowerCase() === 'ativo' ? (
                    <Box className={[styles.status, styles.statusAtivo]}>
                      {row.status}
                    </Box>
                  ) : (
                    <Box className={styles.status}>{row.status}</Box>
                  )}
                </StyledTableCellBody>

                <StyledTableCellBody
                  style={{
                    maxWidth: '220px',
                    overflow: 'hidden',
                    textOverflow: 'hidden',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  <Tooltip title={row?.address}>
                    <span>{row?.address}</span>
                  </Tooltip>
                </StyledTableCellBody>

                <StyledTableCellBody>{row?.phone}</StyledTableCellBody>

                <StyledTableCellBody
                  style={{
                    maxWidth: '220px',
                    overflow: 'hidden',
                    textOverflow: 'hidden',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  <Tooltip title={row?.cpf_cnpj}>
                    <span>{row?.cpf_cnpj}</span>
                  </Tooltip>
                </StyledTableCellBody>

                <StyledTableCellBody>{row?.email}</StyledTableCellBody>
                <StyledTableCellBody>{row?.knowWay}</StyledTableCellBody>

                <StyledTableCellBody align="center">
                  {canUpdate('contatos-financeiros', permissionUser) && (
                    <IconButton
                      aria-label="edit"
                      component={Link}
                      to={`/supplier/${row.id}/edit${location.search}`}
                    >
                      <Icon path={mdiNoteEditOutline} size={1} />
                    </IconButton>
                  )}
                  {canDelete('contatos-financeiros', permissionUser) && (
                    <IconButton onClick={() => handleOpenDeleteModal(row.id)}>
                      <Icon path={mdiDelete} size={1} />
                    </IconButton>
                  )}
                </StyledTableCellBody>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        from={meta?.from}
        to={meta?.to}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        rowsPerPage={Number(meta?.per_page)}
        rows={meta?.total}
        page={Number(meta?.current_page)}
        handleChangePage={onMovePage}
      />

      <BasePopUp
        open={openDeleteModal}
        type="error"
        title="Excluir Fornecedor"
        message="Deseja realmente excluir este fornecedor?"
        handleOpen={handleOpenDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </>
  )
}

export default List
